/**
 * Shapes, grid and automatic layout for the workflow canvas.
 *
 * Kept out of the canvas component because all of it is pure: given steps in,
 * geometry out. That makes the layout rules testable without mounting a canvas,
 * and the canvas file stays about interaction.
 */

import { WorkflowStep } from '@/services/api/workflows';

/** A box, in diagram units. The canvas scales these; nothing here knows zoom. */
export const NODE_W = 200;
export const NODE_H = 80;
/** An expanded box. Taller only — growing sideways would shove neighbours out
 *  of alignment, and the whole point of the grid is that columns line up. */
export const NODE_H_EXPANDED = 200;

export const COL_GAP = 120;
export const ROW_GAP = 80;

/**
 * One grid square.
 *
 * Every dimension above is a whole number of these, which is the property that
 * makes the grid worth having:
 *
 *   NODE_W  200 = 5    NODE_H  80 = 2
 *   column pitch 320 = 8    row pitch 160 = 4
 *
 * So a box snapped to the grid lands in the same column as a box the automatic
 * layout placed, instead of a few units off it. The first version used 20,
 * which divided the width but not the row pitch (88 + 60 = 148), so snapped
 * rows never agreed with laid-out ones — and at 46% zoom a 20-unit mesh is nine
 * screen pixels, too fine to see or to feel.
 */
export const GRID = 40;

/**
 * How close two edges have to be before the box snaps to align with the other.
 *
 * Grid snapping alone does not do what somebody dragging a box is trying to do.
 * They are not aiming at a grid line; they are aiming to line this box up with
 * THAT one. On a 40-unit grid two boxes can sit a full cell apart and still
 * look misaligned, and no amount of grid makes them agree.
 */
export const ALIGN_TOLERANCE = 12;

export type CanvasShape =
  | 'rectangle'
  | 'rounded'
  | 'diamond'
  | 'terminator'
  | 'note'
  | 'parallelogram';

export const CANVAS_SHAPES: CanvasShape[] = [
  'rectangle',
  'rounded',
  'diamond',
  'terminator',
  'note',
  'parallelogram',
];

/**
 * The shape a step is drawn as when nobody has overridden it.
 *
 * One place, so a new step type is drawn sensibly without a data migration and
 * without every caller growing its own opinion. `canvas_shape` on the row wins
 * when set — that is what the palette writes.
 */
export function shapeForStep(step: Pick<WorkflowStep, 'step_type'> & { canvas_shape?: string | null }): CanvasShape {
  const override = (step.canvas_shape || '').trim() as CanvasShape;
  if (override && CANVAS_SHAPES.includes(override)) return override;

  switch (step.step_type) {
    case 'condition':
      // A decision is a diamond. This is the one shape people read without
      // being told, so breaking it would cost more than it saved.
      return 'diamond';
    case 'approval':
      return 'diamond';
    case 'wait':
    case 'wait_for_response':
      return 'rounded';
    case 'api_call':
      return 'parallelogram';
    default:
      return 'rectangle';
  }
}

/** A step that sends the flow two ways, so the canvas draws two edges out. */
export function isBranching(step: Pick<WorkflowStep, 'step_type'>): boolean {
  return step.step_type === 'condition' || step.step_type === 'approval';
}

export function snap(value: number, grid: number = GRID): number {
  return Math.round(value / grid) * grid;
}

export interface SnapResult {
  x: number;
  y: number;
  /** Where to draw a vertical guide, in diagram units, when this box has
   *  locked onto another's left edge, centre or right edge. */
  guideX: number | null;
  guideY: number | null;
}

/**
 * Where a dragged box should actually land.
 *
 * Alignment with a neighbour BEATS the grid, because it is the more specific
 * intent: somebody dragging a box next to another is lining it up with that
 * box, not with an abstract lattice. Only when nothing is near does the grid
 * decide.
 *
 * Three edges are considered on each axis — leading, centre and trailing — so a
 * box can be left-aligned, centred or right-aligned against its neighbour, which
 * between them cover every way two boxes are said to "line up".
 */
export function snapPosition(
  x: number,
  y: number,
  others: Array<{ x: number; y: number; height?: number }>,
  useGrid: boolean,
  height: number = NODE_H
): SnapResult {
  const offsetsX = [0, NODE_W / 2, NODE_W];
  const offsetsY = (h: number) => [0, h / 2, h];

  let bestX: { value: number; guide: number; distance: number } | null = null;
  let bestY: { value: number; guide: number; distance: number } | null = null;

  for (const other of others) {
    const otherH = other.height ?? NODE_H;

    for (const mine of offsetsX) {
      for (const theirs of offsetsX) {
        const target = other.x + theirs;
        const distance = Math.abs(x + mine - target);
        if (distance <= ALIGN_TOLERANCE && (!bestX || distance < bestX.distance)) {
          bestX = { value: target - mine, guide: target, distance };
        }
      }
    }

    for (const mine of offsetsY(height)) {
      for (const theirs of offsetsY(otherH)) {
        const target = other.y + theirs;
        const distance = Math.abs(y + mine - target);
        if (distance <= ALIGN_TOLERANCE && (!bestY || distance < bestY.distance)) {
          bestY = { value: target - mine, guide: target, distance };
        }
      }
    }
  }

  const fallback = (v: number) => (useGrid ? snap(v) : Math.round(v));

  return {
    x: bestX ? Math.round(bestX.value) : fallback(x),
    y: bestY ? Math.round(bestY.value) : fallback(y),
    guideX: bestX ? bestX.guide : null,
    guideY: bestY ? bestY.guide : null,
  };
}

export interface Placed {
  step: WorkflowStep;
  x: number;
  y: number;
  /** False when these coordinates were computed rather than stored, so the
   *  canvas can tell "never arranged" from "arranged here", and a save only
   *  writes boxes somebody actually placed. */
  positioned: boolean;
}

/** Steps up to this many are laid out in one straight run.
 *
 *  Short processes read best as a single left-to-right line, and wrapping five
 *  boxes onto two rows makes a simple thing look complicated. */
export const SINGLE_ROW_MAX = 8;

/**
 * How many columns the automatic layout wraps at.
 *
 * A single row does not fit: thirty steps in a line is 9,760 units wide and 248
 * tall, so fitting it to a 1200-wide viewport scales it to 12% — past the floor,
 * and unreadable long before that. The whole promise is seeing all of it at
 * once, and an aspect ratio that extreme cannot deliver it.
 *
 * So the run wraps to roughly the shape of a screen. Solving
 * `(cols * COL) / (rows * ROW) = 16/9` with `cols * rows = n` gives
 * `cols = sqrt(n * ROW * 16 / (COL * 9))`, which for 30 steps is 5 columns and
 * 6 rows — 1,600 x 888, fitting a 1200x700 viewport at 75%.
 */
export function columnsFor(count: number): number {
  if (count <= SINGLE_ROW_MAX) return count || 1;
  const target = ((NODE_H + ROW_GAP) * 16) / ((NODE_W + COL_GAP) * 9);
  // Rounded, not ceiled. Ceiling always overshoots by up to a whole column,
  // which for 30 steps gave 6 columns and a 2.4:1 diagram — wider than the
  // 16:9 it was solving for, and the wrong side of it.
  return Math.max(1, Math.round(Math.sqrt(count * target)));
}

/**
 * Where every box goes.
 *
 * A step with stored coordinates keeps them. Everything else is laid out left
 * to right in step order, wrapping to the next row at `columnsFor`, which is
 * the arrangement somebody reading a process expects before they have arranged
 * anything themselves.
 *
 * Auto-placed steps are laid out around the stored ones rather than from zero:
 * dropping one new step into an arranged diagram should not push it to the
 * origin and overlap whatever is already there.
 */
export function layout(steps: WorkflowStep[], columns?: number): Placed[] {
  const ordered = [...steps].sort((a, b) => a.step_order - b.step_order);

  const stored = ordered.filter((s) => s.position_x != null && s.position_y != null);
  // Start the automatic run clear of anything already placed, so a new step
  // never lands underneath an arranged one.
  const startY = stored.length
    ? snap(Math.max(...stored.map((s) => (s.position_y as number) + NODE_H)) + ROW_GAP)
    : 0;

  const autoCount = ordered.length - stored.length;
  const cols = Math.max(1, columns ?? columnsFor(autoCount));

  let autoIndex = 0;
  return ordered.map((step) => {
    if (step.position_x != null && step.position_y != null) {
      return { step, x: step.position_x, y: step.position_y, positioned: true };
    }
    const col = autoIndex % cols;
    const row = Math.floor(autoIndex / cols);
    autoIndex += 1;
    return {
      step,
      x: col * (NODE_W + COL_GAP),
      y: startY + row * (NODE_H + ROW_GAP),
      positioned: false,
    };
  });
}

/**
 * The order the diagram reads in: left to right, top to bottom.
 *
 * On the canvas the layout IS the order. Moving a box left of another makes it
 * earlier, which is the only reading of a process diagram anybody brings to it —
 * and the alternative, a number you can only change in a form, means the picture
 * and the process can disagree while both look right.
 *
 * ROWS ARE BANDED ON PURPOSE
 *
 * Comparing raw `y` would let three pixels of drag jitter swap two steps that
 * plainly sit side by side. Boxes within half a row-pitch of each other are
 * treated as the same row and ordered by `x` alone, so only a move somebody
 * clearly meant changes anything.
 *
 * Ties break on the existing order, so a box dropped exactly on top of another
 * does not shuffle unrelated steps.
 */
export function readingOrder(placed: Placed[]): Placed[] {
  const pitch = NODE_H + ROW_GAP;
  const band = (p: Placed) => Math.round(p.y / pitch);
  return [...placed].sort((a, b) => {
    const rowDelta = band(a) - band(b);
    if (rowDelta !== 0) return rowDelta;
    if (a.x !== b.x) return a.x - b.x;
    return a.step.step_order - b.step.step_order;
  });
}

/**
 * What each step's number becomes, given where the boxes now sit.
 *
 * Returns only the steps whose number CHANGES, so the canvas can say how many
 * were renumbered and save nothing it did not need to. An empty map means the
 * arrangement and the order already agree.
 */
export function renumbering(placed: Placed[]): Map<number, number> {
  const changed = new Map<number, number>();
  readingOrder(placed).forEach((p, i) => {
    const next = i + 1;
    if (p.step.step_order !== next) changed.set(p.step.id, next);
  });
  return changed;
}

/**
 * Where an edge leaves one box and enters another, and how it curves.
 *
 * The first version assumed every target sat to the RIGHT of its source: it
 * always left the right edge and entered the left one. On a wrapped diagram —
 * or any diagram somebody has arranged — that is wrong most of the time, and
 * it draws a backwards S that loops out of the source, across its own box and
 * back. It reads as a broken connection rather than as a step that runs next.
 *
 * So the side is chosen from where the boxes actually are:
 *
 *   target clearly to the right   leave right, enter left
 *   target clearly to the left    leave left, enter right   (a return path)
 *   roughly the same column       leave bottom, enter top   (a wrap, or a stack)
 *
 * Heights are passed in because an expanded box is taller, and an edge that
 * still aims at the collapsed centre visibly misses the box it points at.
 */
export interface EdgeAnchors {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  /** Cubic control points, so the curve leaves and enters perpendicular to the
   *  side it touches rather than cutting the corner. */
  c1x: number;
  c1y: number;
  c2x: number;
  c2y: number;
  /** Where a label sits without landing on either box. */
  labelX: number;
  labelY: number;
}

export function edgeAnchors(
  a: { x: number; y: number },
  b: { x: number; y: number },
  aHeight: number = NODE_H,
  bHeight: number = NODE_H
): EdgeAnchors {
  const aMidY = a.y + aHeight / 2;
  const bMidY = b.y + bHeight / 2;
  const aMidX = a.x + NODE_W / 2;
  const bMidX = b.x + NODE_W / 2;

  // "Clearly" to one side means more than half a box apart. Inside that, the
  // boxes are stacked and a sideways edge would run along their edges.
  const sideways = Math.abs(bMidX - aMidX) > NODE_W / 2;

  if (!sideways) {
    const downward = b.y >= a.y;
    const y1 = downward ? a.y + aHeight : a.y;
    const y2 = downward ? b.y : b.y + bHeight;
    const reach = Math.max(28, Math.abs(y2 - y1) / 2);
    return {
      x1: aMidX,
      y1,
      x2: bMidX,
      y2,
      c1x: aMidX,
      c1y: downward ? y1 + reach : y1 - reach,
      c2x: bMidX,
      c2y: downward ? y2 - reach : y2 + reach,
      labelX: (aMidX + bMidX) / 2,
      labelY: (y1 + y2) / 2,
    };
  }

  const rightward = bMidX > aMidX;
  const x1 = rightward ? a.x + NODE_W : a.x;
  const x2 = rightward ? b.x : b.x + NODE_W;
  const reach = Math.max(40, Math.abs(x2 - x1) / 2);
  return {
    x1,
    y1: aMidY,
    x2,
    y2: bMidY,
    c1x: rightward ? x1 + reach : x1 - reach,
    c1y: aMidY,
    c2x: rightward ? x2 - reach : x2 + reach,
    c2y: bMidY,
    labelX: (x1 + x2) / 2,
    labelY: (aMidY + bMidY) / 2 - 8,
  };
}

/** The cubic path an edge draws, from its anchors. */
export function edgePath(k: EdgeAnchors): string {
  return `M ${k.x1} ${k.y1} C ${k.c1x} ${k.c1y}, ${k.c2x} ${k.c2y}, ${k.x2} ${k.y2}`;
}

export interface Bounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
  width: number;
  height: number;
}

/** The extent of the diagram, with room for the start and end caps. */
export function bounds(placed: Placed[], pad = 80): Bounds {
  if (!placed.length) {
    return { minX: 0, minY: 0, maxX: NODE_W, maxY: NODE_H, width: NODE_W, height: NODE_H };
  }
  const minX = Math.min(...placed.map((p) => p.x)) - pad;
  const minY = Math.min(...placed.map((p) => p.y)) - pad;
  const maxX = Math.max(...placed.map((p) => p.x + NODE_W)) + pad;
  const maxY = Math.max(...placed.map((p) => p.y + NODE_H)) + pad;
  return { minX, minY, maxX, maxY, width: maxX - minX, height: maxY - minY };
}

/**
 * The zoom that shows the whole diagram at once.
 *
 * Capped at 1: a four-step workflow blown up to fill a monitor looks broken,
 * and the request was to see everything, not to magnify it. The floor stops a
 * 200-step workflow scaling to nothing — past that point, scrolling a readable
 * diagram beats staring at an unreadable one.
 */
export function fitScale(content: Bounds, viewW: number, viewH: number): number {
  if (!viewW || !viewH || !content.width || !content.height) return 1;
  return Math.max(0.2, Math.min(1, Math.min(viewW / content.width, viewH / content.height)));
}

/**
 * Edges, from the order the steps run in.
 *
 * `depends_on_step_id` wins where it is set, because somebody said so
 * explicitly. Otherwise consecutive steps are joined, which is what the order
 * means. A branching step gets two outgoing edges so a decision reads as a
 * decision rather than as a box with one way out.
 */
export interface Edge {
  from: number;
  to: number;
  label?: string;
  tone: 'default' | 'yes' | 'no';
}

export function edges(steps: WorkflowStep[]): Edge[] {
  const ordered = [...steps].sort((a, b) => a.step_order - b.step_order);
  const byId = new Map(ordered.map((s) => [s.id, s]));
  const out: Edge[] = [];

  ordered.forEach((step, i) => {
    const explicit = step.depends_on_step_id;
    if (explicit && byId.has(explicit)) {
      out.push({ from: explicit, to: step.id, tone: 'default' });
      return;
    }
    const next = ordered[i + 1];
    if (!next) return;

    if (isBranching(step)) {
      out.push({ from: step.id, to: next.id, label: 'Yes', tone: 'yes' });
      // The No path goes to the step after the branch when there is one. With
      // nothing to point at, the edge is left out rather than drawn into
      // nowhere — an arrow to no box is read as a missing step.
      const after = ordered[i + 2];
      if (after) out.push({ from: step.id, to: after.id, label: 'No', tone: 'no' });
      return;
    }
    out.push({ from: step.id, to: next.id, tone: 'default' });
  });

  return out;
}
